import { dateFormat, numberFormat } from "shared/util"
import { TextMatch } from 'shared/text'
import { useState, useMemo } from 'react'
import {
  Stack, StackProps,
  Typography, Chip, Checkbox, Button, FormControlLabel
} from "@mui/material"

import {
  ArticleResult, AuthorType
} from "interface/article"

import { SearchWordField } from 'components/controls/searchWordField'

type EditRefAreticleListProps = {
  articles: ArticleResult[]
  refArticleIdList: number[]
  setRefArticleIdList: (articleIdList: number[]) => void
} & StackProps
export const EditRefAreticleList = ({
  articles,
  refArticleIdList,
  setRefArticleIdList,
  ...others
}: EditRefAreticleListProps) => {
  const [searchWord, setSearchWord] = useState<string>('')
  const [onlyFirstAuthor, setOnlyFirstAuthor] = useState<boolean>(true)

  const filteredArticles = useMemo<{ index: number, article: ArticleResult }[]>(() => {
    const match = (searchWord !== '') ? new TextMatch(searchWord) : null
    return articles.map((article, index) => ({ index, article }))
      .filter((r) => 
        (!onlyFirstAuthor || (r.article.authorType === AuthorType.FirstAuthor || r.article.authorType === AuthorType.SingleAuthor)) &&
        (match?.match(r.article.title, r.article.titleJpn, r.article.abstract, r.article.abstractJpn, r.article.journal.title) ?? true)
      )
  }, [articles, searchWord, onlyFirstAuthor])

  const addRefArticle = (articleId: number) => {
    setRefArticleIdList(refArticleIdList.concat([articleId]))
  }
  const removeRefArticle = (articleId: number) => {
    setRefArticleIdList(refArticleIdList.filter((r) => r !== articleId))
  }
  return (
    <Stack direction='column' spacing={1} {...others}>
      <Stack direction='row' spacing={1} alignItems='center'>
        <Button variant="contained" size='small' onClick={() => setRefArticleIdList([])}>クリア</Button>
        <SearchWordField id='article-search-word' searchWord={searchWord} onChangeSearchWord={setSearchWord} sx={{ flexGrow: 1 }}/>
        <FormControlLabel
          control={
            <Checkbox
              checked={onlyFirstAuthor}
              size="small"
              onChange={(event) => {
                setOnlyFirstAuthor(event.target.checked)
              }}
            />
          }
          label='先頭著者のみ'
        />
      </Stack>
      <Stack direction='column' spacing={0.5}>
        { filteredArticles.map((record, i) => {
          const article = record.article
          const isRef = refArticleIdList.indexOf(article.articleId) >= 0 ? true : false
          return (
            <Stack key={`journal-${article.articleId}-${i}`} direction='row' spacing={1} alignItems='flex-start' flexGrow={1}>
              <Chip
                label={`${record.index + 1}`}
                variant={ isRef ? 'filled' : 'outlined' } color={ isRef ? 'info' : 'default' } size='small' 
                sx={{ cursor: 'pointer', borderRadius: 1, height: 'inherit', minWidth: '2.5em', padding: 0, flexShrink: 0 }}
                onClick={() => isRef ? removeRefArticle(article.articleId) : addRefArticle(article.articleId) }
              />
              <Typography variant="body2" color='text.primary' component="div" flexGrow={1} translate='yes'>
                {article.titleJpn ?? article.title ?? 'No title'}
              </Typography>
              { article.referenceCount > 0 && (
                <Typography variant="body2" color='text.primary' component="div" whiteSpace='nowrap'>{numberFormat(article.referenceCount)}件の参照</Typography>
              )}
              <Typography variant="body2" color='text.secondary' component="div" whiteSpace='nowrap'>{dateFormat(article.revicedDate, { nullText: '--', month: false, day: false, wareki: false })}</Typography>
              { article.authorType === AuthorType.FirstAuthor ? <Chip size='small' variant='filled' label='第一' color='primary' sx={{ height: 'inherit', padding: 0 }} /> :
                article.authorType === AuthorType.LastAuthor ? <Chip size='small' variant='filled' label='責任' color='default' sx={{ height: 'inherit', padding: 0 }} /> :
                article.authorType === AuthorType.SingleAuthor ? <Chip size='small' variant='filled' label='単独' color='secondary' sx={{ height: 'inherit', padding: 0 }} /> : <></> }
            </Stack>
          )
        }) }
      </Stack>
    </Stack>
  )
}
